import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { PrimaryData, SecondaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import { ClaimStatusIndicator, StatusIndicator } from '@strata/react-status-indicators';
import { Tooltip } from '@strata/react-tooltip';

import {
    hasBrandGenericMismatch,
    hasSupplierMismatch,
} from 'components/treatments/claimInsightsFields';
import {
    ClaimInsightProductTypes,
    formatClaimRejectCodes,
    formatDrugDescription,
    getClaimInsightProductType,
    getRejectCodeTooltipDetails,
} from 'services/claimInsightsHelpers';

const formatSupplierLabel = (name, npi) => (name ? `${name} (${npi})` : npi);

const renderClaimInsightsExclamation = (label, tooltipContent) => (
    <Tooltip intent={Tooltip.Intent.WARNING} content={tooltipContent}>
        <span className="font-bold sds_claim-insights-exclamation" aria-label={label}>
            !
        </span>
    </Tooltip>
);

const getProductTypeLabel = (claim) => {
    const productType = getClaimInsightProductType(claim);
    if (productType === ClaimInsightProductTypes.BRAND) {
        return 'Brand';
    }
    if (productType === ClaimInsightProductTypes.GENERIC) {
        return 'Generic';
    }
    return null;
};

const renderRejectCodesTooltip = (claim) => {
    const tooltipLines = getRejectCodeTooltipDetails(claim).filter(
        ({ code, message }) => code || message,
    );

    if (!tooltipLines.length) {
        return undefined;
    }

    return (
        <div>
            {tooltipLines.map(({ code, message }) => (
                <div key={`${code ?? ''}:${message ?? ''}`}>
                    {code && message ? (
                        <>
                            <strong>{code}</strong>
                            {`: ${message}`}
                        </>
                    ) : (
                        (code ?? message)
                    )}
                </div>
            ))}
        </div>
    );
};

export const buildClaimInsightsGridColumns = ({
    claims = [],
    treatmentIsBrand = undefined,
} = {}) => [
    ...(claims.some(({ isTestClaim }) => isTestClaim)
        ? [
              {
                  id: 'testClaimIcon',
                  header: '',
                  size: 40,
                  enableSorting: false,
                  cell: ({ row }) =>
                      row.original.isTestClaim ? (
                          <Icon
                              icon={IconNames.LAB_TEST}
                              intent={Icon.Intent.WARNING}
                              title="Internal test claim"
                              tooltipContent="Internal test claim."
                          />
                      ) : null,
              },
          ]
        : []),
    {
        accessorKey: 'adjudicationDate',
        header: 'Adjudication Date',
        size: 130,
        cell: ({ getValue }) => (
            <PrimaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'claimDateOfService',
        header: 'Claim Date of Service',
        size: 110,
        cell: ({ getValue }) => <PrimaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'claimId',
        header: 'Claim ID',
        size: 110,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'ndc',
        header: 'NDC',
        size: 100,
        cell: ({ row }) => {
            const { ndc, productServiceOid } = row.original;
            if (!ndc) {
                return <SecondaryData data={null} />;
            }

            return (
                <SecondaryData
                    data={ndc}
                    customTooltipContent={
                        <div>
                            <div>{`NDC: ${ndc}`}</div>
                            {productServiceOid ? <div>{`OID: ${productServiceOid}`}</div> : null}
                        </div>
                    }
                />
            );
        },
    },
    {
        accessorKey: 'drugName',
        header: 'Product',
        size: 160,
        cell: ({ row }) => (
            <SecondaryData data={formatDrugDescription(row.original) ?? row.original.drugName} />
        ),
    },
    {
        id: 'brandGeneric',
        header: 'B/G',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => {
            const typeLabel = getProductTypeLabel(row.original);
            const mismatch = hasBrandGenericMismatch(row.original, treatmentIsBrand);

            if (!typeLabel && !mismatch) {
                return <SecondaryData data={null} />;
            }

            return (
                <div className="flex__gap--small" style={{ alignItems: 'center' }}>
                    {typeLabel ? (
                        <StatusIndicator
                            status={StatusIndicator.Status.DEFAULT}
                            message={typeLabel}
                            className="sds_claim-insights-bg-badge"
                        />
                    ) : null}
                    {mismatch ? (
                        <Icon
                            icon={IconNames.WARNING_SIGN}
                            intent={Icon.Intent.WARNING}
                            title="Brand/generic mismatch"
                            tooltipContent="Brand/generic mismatch between claim and referral."
                        />
                    ) : null}
                </div>
            );
        },
    },
    {
        accessorKey: 'drugDaysSupply',
        header: 'Days',
        size: 60,
        cell: ({ getValue }) => <SecondaryData data={getValue()} />,
    },
    {
        accessorKey: 'quantity',
        header: 'Qty',
        size: 60,
        cell: ({ getValue }) => <SecondaryData data={getValue()} />,
    },
    {
        id: 'billingSupplier',
        accessorFn: (claim) =>
            formatSupplierLabel(claim.billingSupplierName, claim.billingSupplierNpi),
        header: 'Billing Supplier',
        size: 180,
        cell: ({ getValue }) => <SecondaryData data={getValue()} />,
    },
    {
        id: 'supplierMismatch',
        header: '',
        size: 40,
        enableSorting: false,
        cell: ({ row }) => {
            const claim = row.original;
            if (!hasSupplierMismatch(claim)) {
                return null;
            }

            return renderClaimInsightsExclamation(
                'Supplier mismatch',
                <div>
                    <div>{`Assigned Supplier: ${formatSupplierLabel(
                        claim.assignedSupplierName,
                        claim.assignedSupplierNpi,
                    )}`}</div>
                    <div>{`Billing Supplier: ${formatSupplierLabel(
                        claim.billingSupplierName,
                        claim.billingSupplierNpi,
                    )}`}</div>
                </div>,
            );
        },
    },
    {
        accessorKey: 'rejectCodes',
        header: 'Reject Codes',
        size: 110,
        cell: ({ row }) => {
            const codesLabel = formatClaimRejectCodes(row.original);
            if (!codesLabel) {
                return <SecondaryData data={null} />;
            }

            return (
                <SecondaryData
                    data={codesLabel}
                    customTooltipContent={renderRejectCodesTooltip(row.original)}
                />
            );
        },
    },
    {
        accessorKey: 'claimStatus',
        header: 'Claim Status',
        size: 130,
        enableSorting: false,
        cell: ({ getValue, row }) =>
            row.original.isTestClaim && !getValue() ? (
                <StatusIndicator
                    status={StatusIndicator.Status.DEFAULT}
                    message="No Response"
                    tooltipText="The PBM returned no disposition — treat as not payable."
                />
            ) : (
                <ClaimStatusIndicator status={getValue()} />
            ),
    },
];
